import React, { useState, useEffect } from 'react';
import { apiClient } from '../services/api';

interface ScheduleEvent {
  id: string;
  title: string;
  description?: string;
  start_time: string;
  end_time?: string;
  location?: string;
  reminder?: number;
}

export default function SchedulePage() {
  const [events, setEvents] = useState<ScheduleEvent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>('list');
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    start_time: '',
    end_time: '',
    location: '',
    reminder: '15',
  });

  useEffect(() => {
    loadEvents();
  }, []);

  const loadEvents = async () => {
    setIsLoading(true);
    const result = await apiClient.getNotes({ category: 'schedule' });
    if (result.data) {
      const eventData = result.data.notes.map((note: any) => ({
        id: note.id,
        title: note.title,
        description: note.content,
        start_time: note.metadata?.start_time || note.created_at,
        end_time: note.metadata?.end_time,
        location: note.metadata?.location,
        reminder: note.metadata?.reminder,
      }));
      setEvents(eventData);
    }
    setIsLoading(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const noteData = {
      title: formData.title,
      content: formData.description,
      category: 'schedule',
      metadata: {
        start_time: formData.start_time,
        end_time: formData.end_time,
        location: formData.location,
        reminder: parseInt(formData.reminder),
      },
    };

    const result = await apiClient.createNote(noteData);
    if (result.data) {
      loadEvents();
      setShowForm(false);
      setFormData({
        title: '',
        description: '',
        start_time: '',
        end_time: '',
        location: '',
        reminder: '15',
      });
    }
  };

  const handleDelete = async (id: string) => {
    if (confirm('确定要删除这个日程吗？')) {
      const result = await apiClient.deleteNote(id);
      if (result.data) {
        setEvents(events.filter(e => e.id !== id));
      }
    }
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('zh-CN', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const isUpcoming = (startTime: string) => {
    return new Date(startTime) > new Date();
  };

  const sortedEvents = [...events].sort((a, b) => 
    new Date(a.start_time).getTime() - new Date(b.start_time).getTime()
  );

  return (
    <div className="schedule-page">
      <div className="schedule-header">
        <h2>📅 日程</h2>
        <div className="header-actions">
          <div className="view-toggle">
            <button
              className={viewMode === 'list' ? 'active' : ''}
              onClick={() => setViewMode('list')}
            >
              列表
            </button>
            <button
              className={viewMode === 'calendar' ? 'active' : ''}
              onClick={() => setViewMode('calendar')}
            >
              日历
            </button>
          </div>
          <button onClick={() => setShowForm(!showForm)} className="add-btn">
            {showForm ? '取消' : '+ 添加日程'}
          </button>
        </div>
      </div>

      {showForm && (
        <div className="schedule-form">
          <h3>添加日程</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>标题 *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="例如：团队会议"
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>开始时间 *</label>
                <input
                  type="datetime-local"
                  value={formData.start_time}
                  onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>结束时间</label>
                <input
                  type="datetime-local"
                  value={formData.end_time}
                  onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                />
              </div>
            </div>

            <div className="form-group">
              <label>地点</label>
              <input
                type="text"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                placeholder="例如：会议室 A"
              />
            </div>

            <div className="form-group">
              <label>描述</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="日程详情"
                rows={3}
              />
            </div>

            <div className="form-group">
              <label>提前提醒</label>
              <select
                value={formData.reminder}
                onChange={(e) => setFormData({ ...formData, reminder: e.target.value })}
              >
                <option value="0">不提醒</option>
                <option value="5">5分钟前</option>
                <option value="15">15分钟前</option>
                <option value="30">30分钟前</option>
                <option value="60">1小时前</option>
                <option value="1440">1天前</option>
              </select>
            </div>

            <button type="submit" className="submit-btn">保存</button>
          </form>
        </div>
      )}

      <div className="schedule-content">
        {isLoading ? (
          <div className="loading">加载中...</div>
        ) : events.length === 0 ? (
          <div className="empty">
            <p>暂无日程</p>
            <button onClick={() => setShowForm(true)}>添加第一个日程</button>
          </div>
        ) : viewMode === 'list' ? (
          <div className="events-list">
            {sortedEvents.map(event => (
              <div
                key={event.id}
                className={`event-item ${isUpcoming(event.start_time) ? 'upcoming' : 'past'}`}
              >
                <div className="event-time">
                  <div className="event-date">
                    {new Date(event.start_time).toLocaleDateString('zh-CN', {
                      month: 'short',
                      day: 'numeric',
                    })}
                  </div>
                  <div className="event-hour">
                    {new Date(event.start_time).toLocaleTimeString('zh-CN', {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </div>
                </div>
                <div className="event-details">
                  <div className="event-title">{event.title}</div>
                  {event.location && (
                    <div className="event-location">📍 {event.location}</div>
                  )}
                  {event.description && (
                    <div className="event-description">{event.description}</div>
                  )}
                  {event.end_time && (
                    <div className="event-duration">
                      至 {formatDateTime(event.end_time)}
                    </div>
                  )}
                </div>
                <button
                  className="delete-btn"
                  onClick={() => handleDelete(event.id)}
                  title="删除"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="calendar-view">
            <p>日历视图开发中...</p>
          </div>
        )}
      </div>

      <style>{`
        .schedule-page {
          padding: 24px;
          max-width: 1000px;
          margin: 0 auto;
        }

        .schedule-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
        }

        .schedule-header h2 {
          font-size: 28px;
          font-weight: 700;
          margin: 0;
          color: #333;
        }

        .header-actions {
          display: flex;
          gap: 12px;
        }

        .view-toggle {
          display: flex;
          background: white;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .view-toggle button {
          padding: 8px 16px;
          border: none;
          background: white;
          cursor: pointer;
          font-size: 14px;
          transition: all 0.2s;
        }

        .view-toggle button.active {
          background: #007bff;
          color: white;
        }

        .add-btn {
          padding: 10px 20px;
          background: #007bff;
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: background 0.2s;
        }

        .add-btn:hover {
          background: #0056b3;
        }

        .schedule-form {
          background: white;
          padding: 24px;
          border-radius: 8px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          margin-bottom: 24px;
        }

        .schedule-form h3 {
          margin: 0 0 20px 0;
          font-size: 18px;
          font-weight: 600;
        }

        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        }

        .form-group {
          margin-bottom: 16px;
        }

        .form-group label {
          display: block;
          margin-bottom: 6px;
          font-size: 14px;
          font-weight: 500;
          color: #333;
        }

        .form-group input,
        .form-group select,
        .form-group textarea {
          width: 100%;
          padding: 8px 12px;
          border: 1px solid #e0e0e0;
          border-radius: 6px;
          font-size: 14px;
          outline: none;
        }

        .form-group input:focus,
        .form-group select:focus,
        .form-group textarea:focus {
          border-color: #007bff;
        }

        .submit-btn {
          width: 100%;
          padding: 12px;
          background: #007bff;
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: background 0.2s;
        }

        .submit-btn:hover {
          background: #0056b3;
        }

        .schedule-content {
          background: white;
          border-radius: 8px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          min-height: 400px;
        }

        .events-list {
          padding: 16px;
        }

        .event-item {
          display: flex;
          gap: 16px;
          padding: 16px;
          border-left: 4px solid #e0e0e0;
          border-radius: 8px;
          margin-bottom: 12px;
          background: #f9f9f9;
          transition: all 0.2s;
        }

        .event-item:hover {
          background: #f5f5f5;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        .event-item.upcoming {
          border-left-color: #007bff;
          background: #f0f8ff;
        }

        .event-item.past {
          opacity: 0.7;
        }

        .event-time {
          text-align: center;
          min-width: 60px;
        }

        .event-date {
          font-size: 14px;
          color: #666;
          margin-bottom: 4px;
        }

        .event-hour {
          font-size: 18px;
          font-weight: 700;
          color: #333;
        }

        .event-details {
          flex: 1;
        }

        .event-title {
          font-size: 16px;
          font-weight: 600;
          margin-bottom: 6px;
          color: #333;
        }

        .event-location {
          font-size: 13px;
          color: #666;
          margin-bottom: 4px;
        }

        .event-description {
          font-size: 13px;
          color: #666;
          margin-bottom: 4px;
        }

        .event-duration {
          font-size: 12px;
          color: #999;
        }

        .delete-btn {
          background: none;
          border: none;
          font-size: 24px;
          color: #999;
          cursor: pointer;
          padding: 0;
          width: 32px;
          height: 32px;
          border-radius: 50%;
          transition: all 0.2s;
          flex-shrink: 0;
        }

        .delete-btn:hover {
          background: #ffebee;
          color: #f44336;
        }

        .loading,
        .empty,
        .calendar-view {
          text-align: center;
          padding: 60px 20px;
          color: #999;
        }

        .empty p {
          font-size: 16px;
          margin-bottom: 16px;
        }

        .empty button {
          padding: 10px 20px;
          background: #007bff;
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 14px;
          cursor: pointer;
          transition: background 0.2s;
        }

        .empty button:hover {
          background: #0056b3;
        }

        @media (max-width: 768px) {
          .schedule-page {
            padding: 16px;
          }

          .schedule-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 12px;
          }

          .form-row {
            grid-template-columns: 1fr;
          }

          .event-item {
            flex-direction: column;
          }

          .event-time {
            display: flex;
            gap: 12px;
            align-items: center;
          }
        }
      `}</style>
    </div>
  );
}
